import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message sent! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">SAANJH</h1>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-4">Contact Us</h1>
          <p className="text-lg text-muted-foreground">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Information Cards */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Our Office</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                <strong className="text-foreground block mb-1">SAANJH</strong>
                Head Office, Sant Nagar,<br />
                Civil Lines,<br />
                Ludhiana - 141001<br />
                Punjab, India
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Phone</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed mb-3">
                <strong className="text-foreground block mb-1">Customer Support</strong>
                <a href="tel:+917837640500" className="text-primary hover:underline">
                  +91 78376 40500
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                Available Monday - Saturday<br />
                9:00 AM - 6:00 PM IST
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Email</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed mb-2">
                <strong className="text-foreground block mb-1">General Inquiries</strong>
                <a href="mailto:admin@saanjh.cc" className="text-primary hover:underline">
                  admin@saanjh.cc
                </a>
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <strong className="text-foreground block mb-1">Support</strong>
                <a href="mailto:support@saanjh.cc" className="text-primary hover:underline">
                  support@saanjh.cc
                </a>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl">Send Us a Message</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="subject" className="text-sm font-medium">
                  Subject *
                </label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="How can we help?"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your inquiry..."
                  rows={6}
                  required
                />
              </div>

              <Button type="submit" className="w-full" size="lg">
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-5xl mx-auto">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2 text-blue-900">Business Hours</h3>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                    Saturday: 10:00 AM - 4:00 PM IST<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <div className="text-green-600 text-xl mt-0.5 flex-shrink-0">🔒</div>
                <div>
                  <h3 className="font-semibold mb-2 text-green-900">Payment Support</h3>
                  <p className="text-sm text-green-800 leading-relaxed">
                    For payment-related queries, our team is here to help. All transactions are 
                    processed securely through Razorpay.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Company Information */}
        <Card className="mt-12 max-w-5xl mx-auto">
          <CardHeader>
            <CardTitle className="text-xl">Company Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-muted-foreground">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-foreground mb-1">Registered Name</p>
                  <p>SAANJH</p>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Business Type</p>
                  <p>E-commerce Platform Provider</p>
                </div>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Registered Address</p>
                <p>Head Office, Sant Nagar, Civil Lines, Ludhiana - 141001, Punjab, India</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Payment Gateway Partner</p>
                <p>Razorpay Software Private Limited (RBI Approved & PCI DSS Certified)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <footer className="border-t py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SAANJH. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;